import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { restoreMongoAtlasState } from '../restoreMongoAtlasWizard';

export const MongoAtlasConfiguration = (
  props: StepProps<restoreMongoAtlasState>
) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { data: projectsResponse } = dal.restore.atlasProjects.useList(
    props.wizardState.restoreAccount!.id
  );

  useEffect(() => {
    if (!props.wizardState.projectId && projectsResponse?.projects) {
      props.setWizardState((state) => ({
        ...state,
        projectId: projectsResponse.projects[0]?.id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsResponse]);

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          isRestoring ||
          !props.wizardState.projectId ||
          !props.wizardState.resourceName
        ) {
          return false;
        }

        return true;
      }}
      onNextClick={async () => {
        setIsRestoring(true);
        setError(undefined);
        const tags = props.wizardState.tags || {};
        tags['eon:restore'] = 'true';

        return dal.restore.atlasProjects
          .restore(
            props.wizardState.resourceId,
            props.wizardState.snapshot.id,
            {
              tags,
              restoreAccountId: props.wizardState.restoreAccount!.id,
              clusterName: props.wizardState.resourceName!,
              atlasProjectId: props.wizardState.projectId!,
            }
          )
          .then(() =>
            props.finish(
              `Started restore job for ${props.wizardState.resourceName}`
            )
          )
          .catch(async (e) => {
            const errorMessage = await extractErrorMessage(e);
            setIsRestoring(false);
            setError(`Failed to restore cluster: ${errorMessage}`);
          });
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mb-[24px] px-[40px]'>
        <Box className='w-full flex justify-between mt-[20px] gap-[36px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Project</FormLabel>
            <Select
              size='small'
              value={
                (projectsResponse?.projects && props.wizardState.projectId) ||
                ''
              }
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  projectId: event.target.value,
                }))
              }
            >
              {(projectsResponse?.projects || []).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl size='small' className='w-full mt-[20px]'>
          <FormLabel>Database name</FormLabel>
          <TextField
            size='small'
            value={props.wizardState.resourceName || ''}
            onChange={(event) =>
              props.setWizardState((state) => ({
                ...state,
                resourceName: event.target.value,
              }))
            }
          />
        </FormControl>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
